import type { ResourceType } from "@medplum/fhirtypes";

import type { DurationUnit } from "@/types/clinic-configuration";

/**
 * Key order is load-bearing: it is the order the unit picker lists its
 * options in. Keep them smallest first.
 */
export const SECONDS_PER_DURATION_UNIT: Record<DurationUnit, number> = {
  minute: 60,
  hour: 3600,
  day: 86400,
};

export const DURATION_UNITS_ASCENDING = Object.keys(
  SECONDS_PER_DURATION_UNIT
) as DurationUnit[];

/**
 * The entry field that heads each card when an array section renders with
 * `presentation: { layout: "section" }`.
 *
 * One convention rather than a per-section setting — see
 * `ConfigSchema.ArrayEntryPresentation` for why naming it in the schema
 * could not be type-checked.
 */
export const CONFIG_ENTRY_TITLE_FIELD = "id";

/**
 * The Follow-ups page's own id scheme — `FUP001`, `FUP002`, ... — passed to
 * `generateSequentialEntryId` as that page's `ConfigSchema.GenerateEntryId`.
 */
export const FOLLOW_UP_ID_PREFIX = "FUP";
export const FOLLOW_UP_ID_DIGITS = 3;

/**
 * The registry entries a page's `count` can name, so the schema types need
 * nothing from the registry itself. Each must load with `projectId` alone,
 * which `clinicConfigurationResources` checks.
 */
export const CLINIC_COUNT_RESOURCES = [
  "practitionerCount",
  "locationCount",
  "serviceCount",
] as const;

/**
 * The digits of a sequential id when its scheme names none: AKB's ids all
 * have three (`LOC001`).
 */
export const SEQUENTIAL_ID_DIGITS = 3;

/**
 * AKB's identifier values: `DOC001` for a practitioner, `LOC001` for a location
 * (`LOC000` is a sheet sentinel AKB never writes), `SVC001` for a service. A
 * record created here continues the same numbers.
 */
export const PRACTITIONER_ID_PREFIX = "DOC";
export const LOCATION_ID_PREFIX = "LOC";
export const HEALTHCARE_SERVICE_ID_PREFIX = "SVC";

/**
 * The search params that hide an inactive record from every clinic list,
 * count and drawer, by resource type. `:not`, not `active=true` or
 * `status=active`: members have no `active`, and AKB writes no `status` on a
 * location, so both would be hidden.
 */
export const CLINIC_ACTIVE_FILTERS = {
  Practitioner: { "active:not": "false" },
  Location: { "status:not": "inactive" },
  HealthcareService: { "active:not": "false" },
} satisfies Partial<Record<ResourceType, Record<string, string>>>;

/**
 * The appointment type of a service created here: the one AKB falls back to
 * when a sheet row names none, and the one most prod services have. The
 * display is stored data, not UI text, so it is not translated.
 */
export const HEALTHCARE_SERVICE_DEFAULT_APPOINTMENT_TYPE = {
  CODE: "office-visit",
  DISPLAY: "Office visit",
  DURATION_MINUTES: 30,
} as const;

/**
 * The anchor on the Kora Behavior page's read-only Overview card. Hand
 * written rather than from `configSectionAnchorId`, since the card is not a
 * schema section — it reports on the stored resource itself.
 *
 * Here rather than in the view because the nav rail links to it and the
 * page renders it, so neither of the two owns it.
 */
export const KORA_OVERVIEW_ANCHOR_ID = "config-overview";

/**
 * The keys the drawer cards that are not schema fields register their
 * drafts under. Each save bar finds its card's section by key and sends it
 * apart from the schema fields.
 */
export const PRACTITIONER_ROLE_GROUP_KEY = "role";
export const SERVICE_LOCATIONS_GROUP_KEY = "locations";
export const LOCATION_SERVICES_GROUP_KEY = "services";
export const SERVICE_PRACTITIONERS_GROUP_KEY = "practitioners";
export const PRACTITIONER_LOCATIONS_GROUP_KEY = "locations";
export const PRACTITIONER_SERVICES_GROUP_KEY = "services";
